import { useEffect, useState, useCallback } from "react";
import { NavLink } from "react-router-dom";
import "../styles/sidebar.css";

// hooks 
import useApiRequestHook from "../hooks/useDetailsRequest";

// types 
import type { PillsType } from '../types/dashboard';


interface MenuItem {
    key: string;
    label: string;
    to?: string;
    icon?: string;
    badge?: (data: PillsType) => string | number | null;
    children?: MenuItem[];
}

const Sidebar = ({ collapsed }: { collapsed: boolean }) => {
    // custom hook  
    const { handleApiRequestHook } = useApiRequestHook();

    // track which submenu keys are open
    const [openKeys, setOpenKeys] = useState<Record<string, boolean>>({});

    const styles = {
        icon: {
            margin: collapsed ? "0 auto" : "0",
        },
    };

    const fetchData = useCallback(async (df: string) => {
        handleApiRequestHook({
            api: '/main-admin/dashboard-pills',
            method: 'GET',
            data: { date_range: df },
            onSuccess: (response) => {
                if (response?.success && response?.data) {
                    if (typeof response?.data === "object") {
                        console.log("response.data", response.data);
                    }
                }
            },
        });
    }, []);


    // useEffect(() => {
    //     fetchData(dateFilter);
    // }, [fetchData]);

    // menu configuration (keeps same routes/labels; add children where needed)
    const menuItems: MenuItem[] = [
        { key: "dashboard", label: "Dashboard", to: "/", icon: "bi bi-house-door" },
        { key: "users", label: "Users", to: "/users", icon: "bi bi-people" },
        { key: "partners", label: "Partners", to: "/partners", icon: "bi bi-shop-window" },
        { key: "new-partners", label: "New Partners", to: "/new-partners", icon: "bi bi-box-arrow-right" },
        { key: "orders", label: "Orders", to: "/orders", icon: "bi bi-bag-check-fill" },
        { key: "categories", label: "Categories", to: "/categories", icon: "bi bi-ui-radios-grid" },
        { key: "coupons", label: "Coupons", to: "/coupons", icon: "bi bi-tags-fill" },

        {
            key: "products",
            label: "Products",
            to: "/products",
            icon: "bi bi-box-fill",
            badge: (d: PillsType) => (d?.items && d.items > 0 ? (d.items > 9 ? "9+" : d.items) : null),
        },

        // Reviews now has submenus
        {
            key: "reviews",
            label: "Reviews",
            icon: "bi bi-star-half",
            badge: (d: PillsType) => {
                const total = (d?.order_reviews ?? 0) + (d?.item_reviews ?? 0) + (d?.partner_reviews ?? 0);
                return total > 0 ? (total > 9 ? "9+" : total) : null;
            },

            children: [
                {
                    key: "reviews-orders",
                    label: "Orders",
                    to: "/reviews/orders",
                    badge: (d: PillsType) => (d?.order_reviews && d.order_reviews > 0 ? (d.order_reviews > 9 ? "9+" : d.order_reviews) : null),
                },
                {
                    key: "reviews-products",
                    label: "Products",
                    to: "/reviews/products",
                    badge: (d: PillsType) => (d?.item_reviews && d.item_reviews > 0 ? (d.item_reviews > 9 ? "9+" : d.item_reviews) : null)
                },
                {
                    key: "reviews-partners",
                    label: "Partners",
                    to: "/reviews/partners",
                    badge: (d: PillsType) => (d?.partner_reviews && d.partner_reviews > 0 ? (d.partner_reviews > 9 ? "9+" : d.partner_reviews) : null)
                },
            ],
        },

        {
            key: "supports",
            label: "Supports",
            to: "/supports",
            icon: "bi bi-headset",
            badge: (d: PillsType) => (d?.supports && d.supports > 0 ? (d.supports > 9 ? "9+" : d.supports) : null),
        },
        {
            key: "bills",
            label: "Bills",
            to: "/bills",
            badge: (d: PillsType) => (d?.partner_bills && d.partner_bills > 0 ? (d.partner_bills > 9 ? "9+" : d.partner_bills) : null),
            icon: "bi bi-cash"
        },
        { key: "earning-report", label: "Earning Report", to: "/earning-report", icon: "bi bi-currency-rupee" },
        { key: "top-entities-report", label: "Top Entities", to: "/top-entities-report", icon: "bi bi-bar-chart-line-fill" },
        { key: "currencies", label: "Currencies", to: "/currencies-exchange", icon: "bi bi-currency-exchange" },
        { key: "admins", label: "Admins", to: "/admins", icon: "bi bi-person-fill-gear" },
        { key: "settings", label: "Settings", to: "/settings", icon: "bi bi-gear" },
    ];

    const toggleOpen = (key: string) => {
        setOpenKeys(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const renderMenuItem = (item: MenuItem) => {
        const hasChildren = Array.isArray(item.children) && item.children.length > 0;
        const isOpen = !!openKeys[item.key];
        const badgeVal = null; //item.badge ? item.badge(pills) : null;

        if (hasChildren) {
            return (
                <li key={item.key} className="sidebar-item-with-children">
                    <div
                        className={`sidebar-link ${isOpen ? "open" : ""}`}
                        onClick={() => {
                            // do not toggle when collapsed (keeps collapsed compact)
                            if (collapsed) return;
                            toggleOpen(item.key);
                        }}
                        style={{ cursor: collapsed ? "default" : "pointer", display: "flex", alignItems: "center" }}
                    >
                        {item.icon && <i className={item.icon} style={styles.icon}></i>}
                        {!collapsed && (
                            <>
                                <span style={{ marginLeft: "10px", flex: 1 }}>{item.label}</span>
                                {badgeVal !== null && badgeVal !== undefined && (
                                    <span className="badge danger" style={{ marginLeft: 8 }}>{badgeVal}</span>
                                )}
                                <i className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"}`} style={{ fontSize: "0.85rem", marginLeft: 8 }}></i>
                            </>
                        )}
                    </div>

                    {/* submenu visible only when not collapsed */}
                    {!collapsed && isOpen && (
                        <ul className="submenu" style={{ listStyle: "none", paddingLeft: 18, marginTop: 6 }}>
                            {item.children!.map(child => {
                                // compute child's badge inline
                                const rawSub = null; //child.badge ? child.badge(pills) : null;
                                let subBadge: string | number | null = null;
                                if (rawSub != null) {
                                    const asNum = Number(rawSub as any);
                                    if (Number.isFinite(asNum)) {
                                        subBadge = asNum > 9 ? "9+" : asNum;
                                    }
                                }

                                return (
                                    <li key={child.key}>
                                        <NavLink
                                            to={child.to || "#"}
                                            className={({ isActive }) => (isActive ? "submenu-link active" : "submenu-link")}
                                            style={{ display: "flex", alignItems: "center", padding: "6px 0" }}
                                        >
                                            <span
                                                style={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: 8,
                                                    display: "inline-block",
                                                    marginRight: 10,
                                                    background: "currentColor",
                                                    opacity: 0.35,
                                                }}
                                            />
                                            <span style={{ flex: 1 }}>{child.label}</span>

                                            {subBadge !== null && subBadge !== undefined && (
                                                <span className="badge danger" style={{ marginLeft: 8 }}>{subBadge}</span>
                                            )}
                                        </NavLink>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </li>
            );
        }

        return (
            <li key={item.key}>
                <NavLink
                    to={item.to || "#"}
                    className={({ isActive }) => (isActive ? "sidebar-link active" : "sidebar-link")}
                    style={{ display: "flex", alignItems: "center", position: "relative" }}
                >
                    {item.icon && <i className={item.icon} style={styles.icon}></i>}
                    {!collapsed && (
                        <>
                            <span style={{ marginLeft: "10px" }}>{item.label}</span>
                            {badgeVal !== null && badgeVal !== undefined && (
                                <span className="badge danger" style={{ marginLeft: 8 }}>{badgeVal}</span>
                            )}
                        </>
                    )}
                </NavLink>
            </li>
        );
    };

    return (
        <aside
            className="sidebar"
            style={{
                width: collapsed ? "60px" : "220px",
                transition: "width 0.15s ease",
            }}
        >
            <nav style={{ paddingTop: "10px", paddingBottom: "10px" }}>
                <ul className="sidebar-nav" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {menuItems.map(renderMenuItem)}
                </ul>
            </nav>
        </aside>
    );
};

export default Sidebar;
